import {
	Area,
	AreaChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import type { ReportingSeries } from "./theme"
import { ReportingChartFrame } from "./chart-frame"
import { ReportingChartTooltip } from "./chart-tooltip"
import {
	reportingBaselineClassName,
	reportingChartTheme,
	reportingGridClassName,
	toneColor,
} from "./theme"

export interface ReportingAreaPoint {
	date: string
	[key: string]: string | number | boolean
}

interface ReportingAreaChartProps {
	title: string
	description?: string
	data: ReportingAreaPoint[]
	series: ReportingSeries[]
	isLoading?: boolean
	icon?: React.ReactNode
	badge?: React.ReactNode
	legend?: React.ReactNode
	className?: string
	height?: number
	valueFormatter?: (value: number) => string
	yDomain?: [number, number] | [number | "auto", number | "auto"]
	yTickFormatter?: (value: number) => string
	tooltip?: React.ReactElement
}

export const ReportingAreaChart = ({
	title,
	description,
	data,
	series,
	isLoading,
	icon,
	badge,
	legend,
	className,
	height = 200,
	valueFormatter = (v) => String(v),
	yDomain,
	yTickFormatter,
	tooltip,
}: ReportingAreaChartProps) => {
	return (
		<ReportingChartFrame
			title={title}
			icon={icon}
			description={description}
			isLoading={isLoading}
			isEmpty={data.length === 0}
			badge={badge}
			legend={legend}
			className={className}
		>
			<div className="w-full [&_*]:outline-none" style={{ height }}>
				<ResponsiveContainer width="100%" height="100%">
					<AreaChart
						data={data}
						margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
					>
						<defs>
							{series.map((s) => (
								<linearGradient
									key={s.key}
									id={`area-${s.key}`}
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop
										offset="0%"
										stopColor={toneColor(s.tone)}
										stopOpacity={reportingChartTheme.areaFillOpacity}
									/>
									<stop
										offset="95%"
										stopColor={toneColor(s.tone)}
										stopOpacity={0}
									/>
								</linearGradient>
							))}
						</defs>
						<CartesianGrid
							className={reportingGridClassName}
							vertical={false}
						/>
						<ReferenceLine y={0} className={reportingBaselineClassName} />
						<XAxis
							dataKey="date"
							tickLine={false}
							axisLine={false}
							height={30}
							tick={{ fontSize: 11 }}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<YAxis
							orientation="right"
							tickLine={false}
							axisLine={false}
							className="text-xs text-gray-500 dark:text-gray-400"
							width={36}
							domain={yDomain}
							tickFormatter={yTickFormatter}
						/>
						<Tooltip
							content={
								tooltip ?? (
									<ReportingChartTooltip
										formatter={(v, name) => [
											valueFormatter(v),
											series.find((s) => s.key === name)?.label ?? name,
										]}
									/>
								)
							}
						/>
						{series.map((s) => (
							<Area
								key={s.key}
								type="linear"
								dataKey={s.key}
								name={s.key}
								stroke={toneColor(s.tone)}
								strokeWidth={2}
								fill={`url(#area-${s.key})`}
								isAnimationActive={false}
							/>
						))}
					</AreaChart>
				</ResponsiveContainer>
			</div>
		</ReportingChartFrame>
	)
}
